/*
 *  Copyright IBM Corp. 2026
 *
 *  This source code is licensed under the Apache-2.0 license found in the
 *  LICENSE file in the root directory of this source tree.
 *
 *  @license
 */

import {
  ChatInstance,
  HistoryItem,
  MessageInputType,
  MessageRequest,
  MessageResponse,
  MessageResponseTypes,
} from "@carbon/ai-chat";

import { pinnedHistoryItems } from "./chatHistoryData";

function generateHistoryItem(isResponse: boolean, text: string): HistoryItem {
  const randomId = `msg-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  if (isResponse) {
    const messageResponse: MessageResponse = {
      id: randomId,
      output: {
        generic: [
          {
            response_type: MessageResponseTypes.TEXT,
            text: text,
          },
        ],
      },
    };

    return {
      message: messageResponse,
      time: new Date().toISOString(),
    };
  } else {
    const messageRequest: MessageRequest = {
      id: randomId,
      input: {
        text,
        message_type: MessageInputType.TEXT,
      },
    };

    return {
      message: messageRequest,
      time: new Date().toISOString(),
    };
  }
}

async function customLoadHistory(
  _instance: ChatInstance,
  requestText = pinnedHistoryItems[1].name,
) {
  const requestTexts = [
    "Can you help me understand this document?",
    "What are the key points from the meeting?",
    "How do I configure the settings?",
    "Show me the latest updates",
    "Explain this concept in simple terms",
    "What's the status of the project?",
    "Can you summarize this for me?",
    "Help me troubleshoot this issue",
  ];

  const responseTexts = [
    "**Bold text** with some *italic* formatting.",
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit.\n\nSed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.\n\n## Heading\n\nDuis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.",
    "Quick single line with `code` snippet.",
    "### Another heading\n\nExcepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.\n\n- Bullet point one\n- Bullet point two\n- Bullet point three",
    "Simple text without formatting.",
    "Medium length paragraph about something interesting. This text should be long enough to span multiple lines but not too overwhelming to read in the chat interface.",
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.\n\n> This is a blockquote with some important information that stands out from the regular text.\n\nUt enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
    "Here's a detailed response with formatting:\n\n1. First point\n2. Second point\n3. Third point\n\nAnd some additional context below.",
  ];

  // Helper functions to get random texts
  const getRandomRequest = () =>
    requestTexts[Math.floor(Math.random() * requestTexts.length)];
  const getRandomResponse = () =>
    responseTexts[Math.floor(Math.random() * responseTexts.length)];

  const history: HistoryItem[] = [];

  // Add initial user message
  history.push(generateHistoryItem(false, requestText));
  // Add conversation with random requests and responses
  history.push(generateHistoryItem(true, getRandomResponse()));
  history.push(generateHistoryItem(true, getRandomResponse()));
  history.push(generateHistoryItem(true, getRandomResponse()));
  history.push(generateHistoryItem(false, getRandomRequest()));
  history.push(generateHistoryItem(true, getRandomResponse()));

  return history;
}

export { customLoadHistory };
